import {SC_SubcontractorFile} from "../files/SC_SubcontractorFile";
import {SC_Address} from "../SC_Address";
import {SC_CrewBase} from "./SC_CrewBase";
import {SC_SubcontractorFilter} from "../filters/SC_SubcontractorFilter";
import {SC_PaginationState} from "../filters/SC_PaginationState";
import {SC_CrewType} from "../enums/SC_CrewType";
import {SC_User} from "../SC_User";

export class SC_Subcontractor extends SC_CrewBase {
    static inputKeys: Record<string, any> = {
        officeContactName: 'officeContactName',
        officeContactEmail: 'officeContactEmail',
        officeContactPhone: 'officeContactPhone',
        mailingAddress: 'mailingAddress',
        officeAddress: 'officeAddress',
        requirements: "requirements",
        autoInsureRequired: 'autoRequired',
        generalInsureRequired: 'generalRequired',
        workCompRequired: 'workRequired',
        files: 'files',
        coiexpiry: 'coiexpiry',
        companyName: 'companyName',
    } as const;

    officeContactName: string;
    officeContactEmail: string;
    officeContactPhone: string;
    mailingAddress: SC_Address | null;
    officeAddress: SC_Address | null;
    autoInsureRequired: boolean;
    generalInsureRequired: boolean;
    workCompRequired: boolean;
    files: SC_SubcontractorFile[];
    coiexpiry: Date | null;
    companyName: string;

    constructor({
                props = {},
                officeContactName = null,
                officeContactEmail = null,
                officeContactPhone = null,
                mailingAddress = new SC_Address(),
                officeAddress = new SC_Address(),
                autoInsureRequired = false,
                generalInsureRequired = false,
                workCompRequired = false,
                files = [],
                coiexpiry = null,
                companyName = null,
            } = {}) {
        super(props);

        this.officeContactName = officeContactName;
        this.officeContactEmail = officeContactEmail;
        this.officeContactPhone = officeContactPhone;
        this.mailingAddress = mailingAddress;
        this.officeAddress = officeAddress;
        this.autoInsureRequired = autoInsureRequired;
        this.generalInsureRequired = generalInsureRequired;
        this.workCompRequired = workCompRequired;
        this.files = files;
        this.coiexpiry = coiexpiry;
        this.companyName = companyName;
    }

    static fromAPI(jsonin: Record<string, any>, usersCache?: SC_User[]): SC_Subcontractor {
        const requirements = jsonin[SC_Subcontractor.inputKeys.requirements] ?? {};
        const out = new SC_Subcontractor({
            props: SC_CrewBase.basicAPIStuff(jsonin),
            officeContactName: jsonin[SC_Subcontractor.inputKeys.officeContactName] ?? null,
            officeContactEmail: jsonin[SC_Subcontractor.inputKeys.officeContactEmail] ?? null,
            officeContactPhone: jsonin[SC_Subcontractor.inputKeys.officeContactPhone] ?? null,
            mailingAddress: jsonin[SC_Subcontractor.inputKeys.mailingAddress] ? SC_Address.fromAPI(jsonin[SC_Subcontractor.inputKeys.mailingAddress]) as SC_Address : null,
            officeAddress: jsonin[SC_Subcontractor.inputKeys.officeAddress] ? SC_Address.fromAPI(jsonin[SC_Subcontractor.inputKeys.officeAddress]) as SC_Address : null,
            autoInsureRequired: requirements[SC_Subcontractor.inputKeys.autoInsureRequired] ?? false,
            generalInsureRequired: requirements[SC_Subcontractor.inputKeys.generalInsureRequired] ?? false,
            workCompRequired: requirements[SC_Subcontractor.inputKeys.workCompRequired] ?? false,
            files: jsonin[SC_Subcontractor.inputKeys.files] ? (jsonin[SC_Subcontractor.inputKeys.files] as Array<Record<string, unknown>>).map(file => SC_SubcontractorFile.fromAPI(file)) : [],
            coiexpiry: jsonin[SC_Subcontractor.inputKeys.coiexpiry] ? new Date(jsonin[SC_Subcontractor.inputKeys.coiexpiry]) : null,
            companyName: jsonin[SC_Subcontractor.inputKeys.companyName] ?? null,
        });

        if (usersCache)
            out.scavengeUsers(usersCache);

        return out;
    }

    toAPI(): Record<string, unknown> {
        return {
            ...super.toAPI(),
            [SC_Subcontractor.inputKeys.officeContactName]: this.officeContactName,
            [SC_Subcontractor.inputKeys.officeContactEmail]: this.officeContactEmail,
            [SC_Subcontractor.inputKeys.officeContactPhone]: this.officeContactPhone,
            [SC_Subcontractor.inputKeys.mailingAddress]: this.mailingAddress?.toAPI(),
            [SC_Subcontractor.inputKeys.officeAddress]: this.officeAddress?.toAPI(),
            [SC_Subcontractor.inputKeys.requirements]: {
                [SC_Subcontractor.inputKeys.autoInsureRequired]: this.autoInsureRequired,
                [SC_Subcontractor.inputKeys.generalInsureRequired]: this.generalInsureRequired,
                [SC_Subcontractor.inputKeys.workCompRequired]: this.workCompRequired,
            },
            [SC_Subcontractor.inputKeys.companyName]: this.companyName,
        };
    }

    clone(): SC_Subcontractor {
        const cloned = new SC_Subcontractor();
        cloned._clone(this);

        cloned.coiexpiry = this.coiexpiry;
        cloned.officeContactName = this.officeContactName;
        cloned.officeContactEmail = this.officeContactEmail;
        cloned.officeContactPhone = this.officeContactPhone;
        cloned.mailingAddress = this.mailingAddress ? this.mailingAddress.clone() : null;
        cloned.officeAddress = this.officeAddress ? this.officeAddress.clone() : null;
        cloned.autoInsureRequired = this.autoInsureRequired;
        cloned.generalInsureRequired = this.generalInsureRequired;
        cloned.workCompRequired = this.workCompRequired;
        cloned.files = this.files.slice().map(file => file.clone());
        cloned.companyName = this.companyName;

        return cloned;
    }

    merge(other: SC_Subcontractor): SC_Subcontractor {
        this._merge(other);

        if (other.officeContactName !== null) this.officeContactName = other.officeContactName;
        if (other.officeContactEmail !== null) this.officeContactEmail = other.officeContactEmail;
        if (other.officeContactPhone !== null) this.officeContactPhone = other.officeContactPhone;
        if (other.companyName !== null) this.companyName = other.companyName;

        if (other.mailingAddress !== null) {
            if (this.mailingAddress === null) {
                this.mailingAddress = other.mailingAddress.clone();
            } else {
                this.mailingAddress.merge(other.mailingAddress);
            }
        }

        if (other.officeAddress !== null) {
            if (this.officeAddress === null) {
                this.officeAddress = other.officeAddress.clone();
            } else {
                this.officeAddress.merge(other.officeAddress);
            }
        }

        if (other.autoInsureRequired !== false) this.autoInsureRequired = other.autoInsureRequired;
        if (other.generalInsureRequired !== false) this.generalInsureRequired = other.generalInsureRequired;
        if (other.workCompRequired !== false) this.workCompRequired = other.workCompRequired;

        return this;
    }

    static getSubs(felter: SC_SubcontractorFilter, pagination: SC_PaginationState, doRequest: (formData: FormData, action: string) => Promise<any>): Promise<{ pages: number, subs: SC_Subcontractor[] }> {
        return new Promise(async (resolve, reject) => {
            let formData = new FormData();

            felter.applyToFormData(formData);

            pagination.applyToFormData(formData);

            let back = await doRequest(formData, "list_subs");

            let out = { pages: back.reason.total, subs: back.reason.subs.map((loc) => SC_Subcontractor.fromAPI(loc)) };

            resolve(out);
        });
    }

    static basicEmpty(): SC_Subcontractor {
        return new SC_Subcontractor({
            props: {
                internalID: -2,
                crewType: new SC_CrewType(SC_CrewType.SUB)
            },
            officeAddress: SC_Address.basicEmpty(),
            mailingAddress: SC_Address.basicEmpty(),
        });
    }

    isDegenerate(): string | null {
        if (!this.alias)
            return "Sub alias is required";
        return null;
    }

    applyToFormData(formData: FormData, oldValue?: SC_Subcontractor) {
        super.applyToFormData(formData, oldValue);

        if (this.officeContactName !== oldValue?.officeContactName && this.officeContactName != null)
            formData.append(SC_Subcontractor.inputKeys.officeContactName, this.officeContactName);
        if (this.officeContactEmail !== oldValue?.officeContactEmail && this.officeContactEmail != null)
            formData.append(SC_Subcontractor.inputKeys.officeContactEmail, this.officeContactEmail);
        if (this.officeContactPhone !== oldValue?.officeContactPhone && this.officeContactPhone != null)
            formData.append(SC_Subcontractor.inputKeys.officeContactPhone, this.officeContactPhone);
        if (this.mailingAddress !== null && (!(oldValue?.mailingAddress ?? false) || JSON.stringify(this.mailingAddress.toAPI()) !== JSON.stringify(oldValue.mailingAddress.toAPI())))
            formData.append(SC_Subcontractor.inputKeys.mailingAddress, JSON.stringify(this.mailingAddress.toAPI()));
        if (this.officeAddress !== null && (!(oldValue?.officeAddress ?? false) || JSON.stringify(this.officeAddress.toAPI()) !== JSON.stringify(oldValue.officeAddress.toAPI())))
            formData.append(SC_Subcontractor.inputKeys.officeAddress, JSON.stringify(this.officeAddress.toAPI()));
        if (this.autoInsureRequired !== oldValue?.autoInsureRequired && this.autoInsureRequired != null)
            formData.append(SC_Subcontractor.inputKeys.autoInsureRequired, String(this.autoInsureRequired));
        if (this.generalInsureRequired !== oldValue?.generalInsureRequired && this.generalInsureRequired != null)
            formData.append(SC_Subcontractor.inputKeys.generalInsureRequired, String(this.generalInsureRequired));
        if (this.workCompRequired !== oldValue?.workCompRequired && this.workCompRequired != null)
            formData.append(SC_Subcontractor.inputKeys.workCompRequired, String(this.workCompRequired));
        if (this.coiexpiry !== oldValue?.coiexpiry && this.coiexpiry != null)
            formData.append(SC_Subcontractor.inputKeys.coiexpiry, this.coiexpiry.toISOString());
        if (this.companyName !== oldValue?.companyName && this.companyName != null)
            formData.append(SC_Subcontractor.inputKeys.companyName, this.companyName);
    }
}
